import cn from "classnames"
import { Link } from "react-router-dom"
import { GetShopByIdResultDto, ShopDto } from "../../models/api/shopApiTypes"
import ShopGallery from "./ShopGallery"
import ShopMap from "./ShopMap"
import ShopsRelated from "./ShopsRelated"

interface ShopDetailsProps {
  shop: GetShopByIdResultDto
  hasPricingList: boolean
}

// Optional shop fields come back as null from the API
const hasValue = (value: string | null | undefined): value is string => !!value && value.trim().length > 0

// Shop owners may enter the website without the protocol
const getWebsiteUrl = (website: string) => (/^https?:\/\//i.test(website) ? website : `https://${website}`)

const getWebsiteLabel = (website: string) => website.replace(/^https?:\/\//i, "").replace(/\/$/, "")

const iconUrl = (name: string) => `${process.env.PUBLIC_URL}/img/icons/${name}.svg`

function BackIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M15 18l-6-6 6-6" />
    </svg>
  )
}

interface ShopContact {
  icon: string
  label: string
  text: string
  href: string
  external: boolean
}

const getShopContacts = (shop: ShopDto): ShopContact[] => {
  const contacts: ShopContact[] = []
  if (hasValue(shop.address))
    contacts.push({
      icon: "location",
      label: "Indirizzo",
      text: shop.address,
      href: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(shop.address)}`,
      external: true,
    })
  if (hasValue(shop.phoneNumber))
    contacts.push({
      icon: "phone",
      label: "Telefono",
      text: shop.phoneNumber,
      href: `tel:${shop.phoneNumber.replace(/[^\d+]/g, "")}`,
      external: false,
    })
  if (hasValue(shop.website))
    contacts.push({
      icon: "website",
      label: "Sito web",
      text: getWebsiteLabel(shop.website),
      href: getWebsiteUrl(shop.website),
      external: true,
    })
  return contacts
}

function ShopContacts({ contacts }: Readonly<{ contacts: ShopContact[] }>) {
  return (
    <ul className="list-unstyled shop-contacts">
      {contacts.map((contact) => (
        <li key={contact.label}>
          <span className="shop-contacts-icon">
            <img src={iconUrl(contact.icon)} alt="" />
          </span>
          <div className="shop-contacts-text">
            <span className="shop-contacts-label">{contact.label}</span>
            <a href={contact.href} {...(contact.external ? { target: "_blank", rel: "noreferrer" } : {})}>
              {contact.text}
            </a>
          </div>
        </li>
      ))}
    </ul>
  )
}

export default function ShopDetails({ shop, hasPricingList }: Readonly<ShopDetailsProps>) {
  const hasPhotos = shop.photos.length > 0
  const hasDescription = hasValue(shop.description)
  const hasAddress = hasValue(shop.address)
  const contacts = getShopContacts(shop)
  const logo = shop.company?.logo

  return (
    <div className="row">
      <div className="shop-page-container">
        <div className="container shop-page">
          {/* On mobile, when there are photos, the back link is a round button over the gallery */}
          <Link to="/negozi" className={cn("shop-back-link", { "d-none d-lg-inline-flex": hasPhotos })}>
            <BackIcon />
            Tutte le attivit&agrave;
          </Link>

          {/* The desktop grid areas change when the left column has no gallery or no content at all */}
          <div
            className={cn("shop-layout", {
              "shop-layout-no-gallery": !hasPhotos && hasDescription,
              "shop-layout-compact": !hasPhotos && !hasDescription,
            })}
          >
            {hasPhotos ? (
              <div className="shop-layout-gallery">
                <Link to="/negozi" className="shop-back-overlay d-lg-none" aria-label="Tutte le attività">
                  <BackIcon />
                </Link>
                <ShopGallery photos={shop.photos} />
              </div>
            ) : null}

            <aside className="shop-info">
              <div className="shop-info-header">
                {hasValue(logo) ? <img src={logo} className="shop-info-logo" alt="" /> : null}
                <div>
                  <h1 className="shop-info-title">{shop.name}</h1>
                  <div className="shop-info-meta">
                    {hasValue(shop.category) ? <span className="shop-info-category">{shop.category}</span> : null}
                    {hasValue(shop.town) ? <span>{shop.town}</span> : null}
                  </div>
                </div>
              </div>

              <div className="shop-info-actions">
                {hasPricingList ? (
                  <Link to={`/negozio/${shop.id}/listino-prezzi`} className="btn btn-green">
                    <img src={iconUrl("pricing-list")} alt="" />
                    Sfoglia il listino prezzi
                  </Link>
                ) : null}
                <Link
                  to="/acquista-gift-card"
                  className={cn("btn", hasPricingList ? "btn-green-outline" : "btn-green")}
                >
                  <img src={iconUrl("gift")} alt="" />
                  Regala una gift card
                </Link>
              </div>

              {contacts.length > 0 ? <ShopContacts contacts={contacts} /> : null}
            </aside>

            {hasDescription ? (
              <section className="shop-layout-about">
                <h2 className="shop-section-title">Chi siamo</h2>
                <p className="shop-description">{shop.description}</p>
              </section>
            ) : null}

            {hasAddress ? (
              <section className="shop-layout-map">
                <h2 className="shop-section-title">Dove siamo</h2>
                <ShopMap address={shop.address} />
                <p className="shop-map-address">{shop.address}</p>
              </section>
            ) : null}
          </div>

          {shop.related.length > 0 ? <ShopsRelated related={shop.related} /> : null}
        </div>
      </div>
    </div>
  )
}
